import type { SelectedAppointmentSummary, TransferTarget, TransferTargetOption } from "../../types/index.type";

// Polling while the backend reports the schedule is still loading. For the
// first TARGET_SETTLE_CAP_MS the button shows a shimmer and polls quickly.
// After that it reads "Transfer to EHR" (the click takes today's path, so
// nobody waits on this) and keeps polling quietly, slower, until the backend's
// own prefetch retries have had time to finish — a schedule that lands late
// still upgrades the button to the suggestion.
export const TARGET_POLL_INTERVAL_MS = 2000;
export const TARGET_SETTLE_CAP_MS = 8000;
export const TARGET_BACKGROUND_CAP_MS = 5 * 60_000;

export const targetPollDelay = (elapsedMs: number): number => {
  if (elapsedMs < TARGET_SETTLE_CAP_MS) return TARGET_POLL_INTERVAL_MS;
  if (elapsedMs < 60_000) return 5000;
  return 15_000;
};

// A failed request to the target endpoint (network blip, 5xx) is retried with
// these waits before the button falls back to what it last knew.
export const TARGET_FETCH_RETRY_DELAYS_MS = [2000, 4000, 8000];

// One note's target as the provider holds it. `target: null` with
// `settled: true` means "render today's button" (endpoint missing, fetch
// failed after retries). `slow`: still loading past the settle cap — the
// button reads as today's while polling continues in the background.
export interface TargetEntry {
  target: TransferTarget | null;
  fetching: boolean;
  settled: boolean;
  firstRequestedAt: number | null;
  slow: boolean;
  errorCount: number;
}

export const EMPTY_ENTRY: TargetEntry = {
  target: null,
  fetching: false,
  settled: false,
  firstRequestedAt: null,
  slow: false,
  errorCount: 0,
};

// Why the schedule couldn't be read, in words (reason codes from the
// backend's prefetch; never EHR text). The transfer itself still works.
export const unavailableCopy = (reason: string | null | undefined): string => {
  switch (reason) {
    case "login_failed":
      return "Couldn’t sign in to the EHR to read the schedule.";
    case "mfa_required":
    case "mfa_failed":
      return "The EHR asked for a verification code, so the schedule couldn’t be read ahead of time.";
    case "patient_not_found":
    case "no_patient_id":
      return "Couldn’t find this patient in the EHR. Check the MRN/Patient ID.";
    default:
      return "Couldn’t load the schedule.";
  }
};

// Whether the split button renders at all. Anything short of a positive,
// enabled block is today's button — including `not_applicable` (no MRN, no
// credential, an EHR whose skill skips appointments), which has no schedule.
export const isSplitEnabled = (entry: TargetEntry | null | undefined): boolean =>
  !!entry?.target?.enabled && entry.target.state !== "not_applicable";

export const suggestedOption = (target: TransferTarget | null | undefined): TransferTargetOption | null => {
  if (!target || target.state !== "ready" || !target.suggested_id) return null;
  return target.options.find((o) => o.id === target.suggested_id) ?? null;
};

// Where a one-click sends this note: the clinician's choice (the Encounter
// field) when there is one, else the suggestion. Every surface uses this, so
// the note button and Superdoc can never name two different appointments.
export const effectiveOption = (target: TransferTarget | null | undefined): TransferTargetOption | null => {
  if (target?.state === "ready" && target.selected_id) {
    const chosen = target.options.find((o) => o.id === target.selected_id);
    if (chosen) return chosen;
  }
  return suggestedOption(target);
};

// PostHog properties for a click aimed at `appointmentId`: flags and counts
// about the schedule the clinician saw, never ids or patient data.
// `changed_from_suggestion` is the headline: they overrode what we suggested.
export const transferClickProperties = (target: TransferTarget | null | undefined, appointmentId: string | null) => {
  const picked = appointmentId ? target?.options.find((o) => o.id === appointmentId) ?? null : null;
  const suggestedId = target?.state === "ready" ? target.suggested_id ?? null : null;
  return {
    has_target: !!appointmentId,
    target_state: target?.state ?? "none",
    option_count: target?.options.length ?? 0,
    no_default_reason: target?.no_default_reason ?? null,
    had_suggestion: !!suggestedId,
    changed_from_suggestion: !!appointmentId && !!suggestedId && appointmentId !== suggestedId,
    picked_distance_days: picked?.distance_days ?? null,
    picked_compile: picked?.compile ?? null,
    picked_has_note: picked ? !!picked.transferred_note_id : null,
    picked_signed: picked ? !!picked.signed : null,
  };
};

// "Today 2:30 PM" / "Thu Sep 17" — the compact target label.
// `time_label` is formatted server-side ("2:30 PM", "" when the row has no
// real time); the raw EHR `time` ("14:30:00") is only a fallback.
export const optionLabel = (option: {
  day_label?: string;
  time?: string;
  time_label?: string;
  date?: string;
}): string => {
  const day = option.day_label || option.date || "";
  const time = option.time_label ?? option.time;
  return [day, time].filter(Boolean).join(" ").trim();
};

export const summaryLabel = (summary: SelectedAppointmentSummary | null | undefined): string =>
  summary ? optionLabel(summary) : "";

// The whisper under a "Choose appointment…" button, naming why nothing was
// defaulted. Reads off the nearest row so the clinician sees the actual gap.
export const noDefaultCopy = (target: TransferTarget): string => {
  switch (target.no_default_reason) {
    case "too_far": {
      const nearest = target.options.find((o) => o.distance_days != null);
      if (nearest?.distance_days != null) {
        const n = Math.abs(nearest.distance_days);
        const when = nearest.distance_days < 0 ? "before" : "after";
        return `Nearest appointment is ${nearest.day_label || nearest.date}, ${n} day${n === 1 ? "" : "s"} ${when} this note`;
      }
      return "No appointment near this note's date";
    }
    case "ambiguous":
      return "More than one appointment that day — choose one";
    case "none_eligible":
      return "No open appointment in the last two weeks";
    default:
      return "";
  }
};

export type ButtonViewKind = "suggest" | "choose" | "plain" | "loading";

export interface ButtonView {
  kind: ButtonViewKind;
  // Primary text ("Transfer to", "Choose appointment…", "Transferring…").
  text: string;
  // The lighter target segment ("Today 2:30 PM"); empty when none.
  sub: string;
  // Small count badge on the caret (only when > 1 option).
  badge: number | null;
  // Whether the caret half is clickable.
  caretEnabled: boolean;
  // Quiet line under the button; empty when none.
  whisper: string;
}

interface ViewInput {
  entry: TargetEntry;
  // A failed or cancelled attempt: the button reads "Retry Transfer".
  retrying: boolean;
  lastRequestedId: string | null;
}

// The row Retry should aim at: the appointment the failed attempt was
// one-clicked to, if it is still on the schedule. Otherwise nothing — Retry
// then takes today's path (the picker appears when there is a choice), so a
// job cancelled at the picker is never silently re-aimed at the suggestion.
export const pickRetryTarget = (target: TransferTarget | null, lastRequestedId: string | null): string | null => {
  if (!target || target.state !== "ready" || !lastRequestedId) return null;
  return target.options.some((o) => o.id === lastRequestedId) ? lastRequestedId : null;
};

// Label matrix for the split button. It only renders for a note that can be
// transferred now (never started, or retrying), so job status is not an input.
export const buttonView = ({ entry, retrying, lastRequestedId }: ViewInput): ButtonView => {
  const target = entry.target;
  const optionCount = target?.options.length ?? 0;
  const badge = optionCount > 1 ? optionCount : null;

  if (retrying) {
    const retryId = pickRetryTarget(target, lastRequestedId);
    const retryOption = retryId ? target?.options.find((o) => o.id === retryId) ?? null : null;
    return {
      kind: retryOption ? "suggest" : "plain",
      text: "Retry Transfer",
      sub: retryOption ? optionLabel(retryOption) : "",
      badge,
      caretEnabled: !!target && target.state === "ready" && optionCount > 0,
      whisper: "",
    };
  }

  // Still loading past the settle cap: today's label (the click takes today's
  // path); the caret says it is still reading. Polling carries on meanwhile.
  const stillLoading = !target || target.state === "loading" || (!entry.settled && entry.fetching);
  if (stillLoading && entry.slow)
    return { kind: "plain", text: "Transfer to EHR", sub: "", badge: null, caretEnabled: true, whisper: "" };
  if (stillLoading)
    return { kind: "loading", text: "Transfer to", sub: "", badge: null, caretEnabled: false, whisper: "" };

  if (target.state === "ready") {
    const suggestion = effectiveOption(target);
    if (suggestion) {
      return {
        kind: "suggest",
        text: "Transfer to",
        sub: optionLabel(suggestion),
        badge,
        caretEnabled: true,
        whisper:
          suggestion.compile === "pending"
            ? "Preparing transfer…"
            : suggestion.transferred_note_id
              ? "This appointment already has a note from another recording"
              : "",
      };
    }
    if (optionCount > 0) {
      return {
        kind: "choose",
        text: "Choose appointment…",
        sub: "",
        badge,
        caretEnabled: true,
        whisper: noDefaultCopy(target),
      };
    }
  }
  // empty / unavailable / ready-with-no-rows: today's label, caret explains.
  return { kind: "plain", text: "Transfer to EHR", sub: "", badge: null, caretEnabled: true, whisper: "" };
};

export interface DayGroup {
  label: string;
  options: TransferTargetOption[];
}

// Menu rows grouped by day, groups in the order their first row appears
// (the backend ranks this note's candidates first), rows in server order.
// Buckets across the whole list: ranking by distance to the recording can
// interleave days, and a day must never appear as two groups.
export const groupByDay = (options: TransferTargetOption[]): DayGroup[] => {
  const groups = new Map<string, DayGroup>();
  for (const option of options) {
    const label = option.day_label || "Other";
    const group = groups.get(label) ?? { label, options: [] };
    group.options.push(option);
    groups.set(label, group);
  }
  return Array.from(groups.values());
};

// "Updated just now" / "Updated 3 min ago" / "Updated 2 h ago".
export const freshnessCopy = (fetchedAtSeconds: number | null | undefined, now: number = Date.now()): string => {
  if (!fetchedAtSeconds) return "";
  const ageS = Math.max(0, Math.round(now / 1000 - fetchedAtSeconds));
  if (ageS < 60) return "Updated just now";
  if (ageS < 3600) return `Updated ${Math.round(ageS / 60)} min ago`;
  if (ageS < 86400) return `Updated ${Math.round(ageS / 3600)} h ago`;
  return `Updated ${Math.round(ageS / 86400)} d ago`;
};
